import { useState } from 'react';

import { PencilEdit01Icon } from '@hugeicons/core-free-icons';
import { AdminPermissions } from 'constants/admin-permissions';
import { useDocumentTitle } from 'hooks/document-title';
import { useToast } from 'hooks/toast';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';

import { EmailTemplateModal } from 'components/EmailTemplateModal/EmailTemplateModal';
import { SearchInput, Select } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';

import {
  useCreateEmailTemplateDefaultMutation,
  useGetEmailTemplateDefaultsQuery,
  useUpdateEmailTemplateDefaultMutation,
} from 'api/email-template-defaults-api';

const CATEGORY_DOT_COLOR = {
  candidate: 'bg-green-2',
  referee: 'bg-yellow-2',
};

const AdminEmailTemplateDefaults = () => {
  useDocumentTitle('Email Template Defaults');

  const { t } = useTranslation();
  const showToast = useToast();

  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const queryParams = {};
  if (categoryFilter) queryParams.category = categoryFilter;
  if (searchQuery.trim()) queryParams.search = searchQuery.trim();

  const {
    data: templates = [],
    isLoading,
    isFetching,
  } = useGetEmailTemplateDefaultsQuery(queryParams);
  const [createEmailTemplateDefault, { isLoading: isCreating }] =
    useCreateEmailTemplateDefaultMutation();
  const [updateEmailTemplateDefault, { isLoading: isUpdating }] =
    useUpdateEmailTemplateDefaultMutation();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const openCreate = () => {
    setEditingTemplate(null);
    setModalVisible(true);
  };

  const openEdit = template => {
    setEditingTemplate(template);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingTemplate(null);
  };

  const handleSubmit = async data => {
    const result = editingTemplate
      ? await updateEmailTemplateDefault({ id: editingTemplate.id, ...data })
      : await createEmailTemplateDefault(data);

    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.saveFailed'), 'error');
      return;
    }
    closeModal();
  };

  const hasActiveFilters = Boolean(categoryFilter || searchQuery.trim());
  const showInitialEmpty = !isLoading && templates.length === 0 && !hasActiveFilters;
  const showFilteredEmpty = !isLoading && templates.length === 0 && hasActiveFilters;

  return (
    <AdminPage
      loading={isLoading}
      title="Email Template Defaults"
      button
      buttonText={t('emailTemplates.createButton')}
      onClick={openCreate}
      allowedRole={AdminPermissions.UpdateEmailTemplates}>
      <EmailTemplateModal
        visible={modalVisible}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialValues={editingTemplate}
        isLoading={isCreating || isUpdating}
      />

      <div className="bg-neutral-2 rounded-3xl shadow-sm border border-neutral-4 overflow-hidden u-margin--large">
        <div className="flex flex-col gap-6 bg-neutral-1 p-8 border-b border-neutral-4">
          <p className="text-xl text-neutral-6">{t('emailTemplates.subtitle')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              placeholder={t('emailTemplates.filters.searchPlaceholder')}
              value={searchQuery}
              onChange={setSearchQuery}
              className="min-w-[200px] flex-1 max-w-md"
            />
            <Select
              name="categoryFilter"
              className="w-48 min-w-[12rem]"
              value={categoryFilter}
              inputProps={{ onChange: e => setCategoryFilter(e.target.value) }}
              placeholder={t('emailTemplates.filters.allCategories')}>
              <Select.Item value="">{t('emailTemplates.filters.allCategories')}</Select.Item>
              <Select.Item value="candidate">{t('emailTemplates.category.candidate')}</Select.Item>
              <Select.Item value="referee">{t('emailTemplates.category.referee')}</Select.Item>
            </Select>
          </div>
        </div>

        {(isLoading || isFetching) && templates.length === 0 && (
          <p className="text-sm text-neutral-5 p-8">{t('common.loading')}</p>
        )}

        {showInitialEmpty && (
          <p className="text-sm text-neutral-5 mt-4 p-8">{t('emailTemplates.empty')}</p>
        )}

        {showFilteredEmpty && (
          <p className="text-sm text-neutral-5 mt-4 p-8">{t('emailTemplates.noResults')}</p>
        )}

        {!isLoading && templates.length > 0 && (
          <div className="p-8">
            <Table.Context>
              <Table.TableContent className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <tr>
                      <Table.Header noBorder>{t('emailTemplates.table.name')}</Table.Header>
                      <Table.Header noBorder>{t('emailTemplates.table.category')}</Table.Header>
                      <Table.Header noBorder>{t('emailTemplates.table.type')}</Table.Header>
                      <Table.Header noBorder>{t('emailTemplates.table.subject')}</Table.Header>
                      <Table.Header noBorder>{t('emailTemplates.table.updated')}</Table.Header>
                      <Table.Header noBorder align="end">
                        {t('emailTemplates.table.actions')}
                      </Table.Header>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {templates.map(template => (
                      <Table.Row key={template.id}>
                        <Table.Col className="text-xl font-medium text-neutral-7">
                          {template.name}
                        </Table.Col>
                        <Table.Col>
                          <StatusPill
                            status="neutral"
                            text={
                              template.category === 'candidate'
                                ? t('emailTemplates.category.candidate')
                                : t('emailTemplates.category.referee')
                            }
                            colorDot={CATEGORY_DOT_COLOR[template.category] || 'bg-neutral-4'}
                            className="px-4"
                          />
                        </Table.Col>
                        <Table.Col className="text-xl font-medium text-neutral-6">
                          {template.type
                            ? t(`emailTemplates.type.${template.type}`, {
                                defaultValue: template.type,
                              })
                            : '—'}
                        </Table.Col>
                        <Table.Col>
                          <span className="text-xl font-medium text-neutral-6 max-w-xs truncate inline-block align-bottom">
                            {template.subject}
                          </span>
                        </Table.Col>
                        <Table.Col>
                          <span className="text-xl font-medium text-neutral-6 whitespace-nowrap">
                            <Moment fromNow>{template.updated_at || template.created_at}</Moment>
                          </span>
                        </Table.Col>
                        <Table.Col align="right">
                          <div className="d-flex align-items-center justify-content-end">
                            <IconButton
                              className="u-margin-right--small"
                              icon={PencilEdit01Icon}
                              prefix="hugeicons"
                              tip={t('common.edit')}
                              onClick={() => openEdit(template)}
                              placement="top"
                            />
                          </div>
                        </Table.Col>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Table.TableContent>
            </Table.Context>
          </div>
        )}
      </div>
    </AdminPage>
  );
};

export default AdminEmailTemplateDefaults;
